import { Box, Button, ButtonBase, FormControlLabel, Grid, Switch, Typography, useMediaQuery } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Add, ChevronRight, LocalOffer } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import type { CupomItem } from "../models/cupomEstabelecimentoFornecedorModel"
import { useCupomEstabelecimentoFornecedorContext } from "../context/useCupomEstabelecimentoFornecedorContext"
import { CupomDialog } from "./CupomDialog"
import { descreverDesconto, estaExpirado, formatarData, tipoDoCupom } from "./cupomUtils"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: theme.spacing(1),
            marginBottom: theme.spacing(3),
        },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    addButton: {
        borderRadius: 20,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        padding: theme.spacing(0.75, 2.5),
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    card: {
        width: "100%",
        height: "100%",
        display: "block",
        textAlign: "left",
        backgroundColor: "white",
        borderRadius: 16,
        padding: theme.spacing(2, 2, 1.5),
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        transition: "box-shadow .15s, transform .15s",
        "&:hover": {
            boxShadow: "0 6px 24px rgba(0,0,0,0.14)",
            transform: "translateY(-1px)",
        },
    },
    cardInativo: {
        opacity: 0.6,
    },
    cardTopo: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.25),
    },
    codigo: {
        fontWeight: 700,
        fontSize: "1.05rem",
        color: "#2c3e50",
        letterSpacing: 0.5,
        flexGrow: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    desconto: {
        color: "#2e7d32",
        fontWeight: 600,
        fontSize: "0.85rem",
        marginBottom: theme.spacing(1.5),
    },
    selo: {
        fontSize: "0.7rem",
        fontWeight: 600,
        borderRadius: 10,
        padding: "2px 8px",
        whiteSpace: "nowrap",
    },
    seloInativo: {
        backgroundColor: "#eceff1",
        color: "#607d8b",
    },
    seloUso: {
        color: "#7f8c8d",
        fontSize: "0.75rem",
        whiteSpace: "nowrap",
    },
    dados: {
        borderTop: "1px solid #eef0f2",
        paddingTop: theme.spacing(1),
    },
    rotulo: {
        fontSize: "0.7rem",
        color: "#95a5a6",
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },
    valor: {
        fontSize: "0.85rem",
        color: "#2c3e50",
        fontWeight: 500,
    },
    seloExpirado: {
        backgroundColor: "#fff4e5",
        color: "#b26a00",
    },
    validade: {
        fontSize: "0.75rem",
        color: "#95a5a6",
        marginTop: theme.spacing(1),
    },
    secaoExpirados: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2),
        color: "#7f8c8d",
        "&:after": {
            content: '""',
            flexGrow: 1,
            borderTop: "1px solid #e0e0e0",
        },
    },
    filtro: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: theme.spacing(-2),
        marginBottom: theme.spacing(1),
        "& .Mui-checked": { color: "#4caf50" },
        "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" },
        [theme.breakpoints.down("sm")]: { justifyContent: "flex-start", marginTop: 0 },
    },
    emptyText: {
        textAlign: "center",
        color: "#7f8c8d",
        padding: theme.spacing(2, 4, 3),
    },
}))

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
    const classes = useStyles()
    return (
        <Grid item xs={6}>
            <Typography className={classes.rotulo}>{rotulo}</Typography>
            <Typography className={classes.valor}>{valor}</Typography>
        </Grid>
    )
}

function CupomResumo({ cupom, onClick, disabled }: { cupom: CupomItem; onClick: () => void; disabled: boolean }) {
    const classes = useStyles()
    const tipo = tipoDoCupom(cupom)
    const nomeTipo = tipo === "percentual" ? "Percentual (%)" : tipo === "valor" ? "Valor (R$)" : "Frete grátis"
    const expirado = estaExpirado(cupom)
    return (
        <ButtonBase
            component="div"
            className={`${classes.card} ${cupom.ativo && !expirado ? "" : classes.cardInativo}`}
            onClick={onClick}
            disabled={disabled}
        >
            <div className={classes.cardTopo}>
                <Typography className={classes.codigo}>{cupom.nomeCupom}</Typography>
                {expirado && <span className={`${classes.selo} ${classes.seloExpirado}`}>Expirado</span>}
                {!cupom.ativo && <span className={`${classes.selo} ${classes.seloInativo}`}>Inativo</span>}
                {cupom.quantidadeUtilizada > 0 && (
                    <span className={classes.seloUso}>Usado {cupom.quantidadeUtilizada}x</span>
                )}
                <ChevronRight style={{ color: "#bdc3c7" }} />
            </div>
            <Typography className={classes.desconto}>{descreverDesconto(cupom)}</Typography>
            <Grid container spacing={1} className={classes.dados}>
                <Dado rotulo="Tipo do cupom" valor={nomeTipo} />
                <Dado rotulo="Frete grátis" valor={cupom.freteGratis ? "Sim" : "Não"} />
                <Dado rotulo="Aplicação" valor={cupom.automatico ? "Automática" : "Digitada"} />
                <Dado rotulo="Primeira compra" valor={cupom.apenasPrimeiraCompra ? "Sim" : "Não"} />
            </Grid>
            {cupom.fimCupomTimestamp !== null && (cupom.validadeDias ?? 0) < 3650 && (
                <Typography className={classes.validade}>
                    {expirado ? "Expirou em" : "Válido até"} {formatarData(cupom.fimCupomTimestamp)}
                </Typography>
            )}
        </ButtonBase>
    )
}

const CHAVE_MOSTRAR_EXPIRADOS = "cuponsMostrarExpirados"

function GradeCupons({ cupons, onAbrir, disabled }: {
    cupons: CupomItem[]
    onAbrir: (c: CupomItem) => void
    disabled: boolean
}) {
    return (
        <Grid container spacing={2}>
            {cupons.map(cupom => (
                <Grid item xs={12} sm={6} key={cupom.localId}>
                    <CupomResumo cupom={cupom} onClick={() => onAbrir(cupom)} disabled={disabled} />
                </Grid>
            ))}
        </Grid>
    )
}

export function TabelaCupons() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, erroCarregamento } = useCupomEstabelecimentoFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const [dialogAberto, setDialogAberto] = useState(false)
    const [cupomEdicao, setCupomEdicao] = useState<CupomItem | null>(null)
    const [mostrarExpirados, setMostrarExpirados] = useState(() => {
        try {
            return localStorage.getItem(CHAVE_MOSTRAR_EXPIRADOS) === "1"
        } catch {
            return false
        }
    })

    const alternarExpirados = (v: boolean) => {
        setMostrarExpirados(v)
        try {
            localStorage.setItem(CHAVE_MOSTRAR_EXPIRADOS, v ? "1" : "0")
        } catch {
            // preferência só desta estação; sem storage, vale até recarregar
        }
    }

    const agora = Date.now()
    const vigentes = dados.cupons.filter(c => !estaExpirado(c, agora))
    const expirados = dados.cupons.filter(c => estaExpirado(c, agora))

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    const abrirNovo = () => {
        setCupomEdicao(null)
        setDialogAberto(true)
    }

    const abrirEdicao = (cupom: CupomItem) => {
        setCupomEdicao(cupom)
        setDialogAberto(true)
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Box className={classes.header}>
                    <Box>
                        <Typography variant="h4" className={classes.titleText}>
                            Cupons de Desconto
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                            Cadastre cupons de desconto para incentivar os seus clientes
                        </Typography>
                    </Box>
                    <Button
                        className={classes.addButton}
                        startIcon={<Add />}
                        onClick={abrirNovo}
                        disabled={!!erroCarregamento}
                        fullWidth={isMobile}
                    >
                        Novo Cupom
                    </Button>
                </Box>

                {expirados.length > 0 && (
                    <Box className={classes.filtro}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={mostrarExpirados}
                                    onChange={e => {
                                        const v = e.target.checked
                                        alternarExpirados(v)
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                    Mostrar cupons expirados ({expirados.length})
                                </Typography>
                            }
                        />
                    </Box>
                )}

                {vigentes.length === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            borderRadius: 20,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            backgroundColor: "white",
                            padding: "48px 24px",
                        }}
                    >
                        <LocalOffer style={{ fontSize: 48, color: "#bdc3c7", marginBottom: 16 }} />
                        <Typography className={classes.emptyText}>
                            {expirados.length > 0
                                ? "Nenhum cupom dentro da validade. Clique em \"Novo Cupom\" para adicionar ou reative um cupom expirado."
                                : "Nenhum cupom cadastrado. Clique em \"Novo Cupom\" para adicionar."}
                        </Typography>
                    </Box>
                ) : (
                    <GradeCupons cupons={vigentes} onAbrir={abrirEdicao} disabled={!!erroCarregamento} />
                )}

                {mostrarExpirados && expirados.length > 0 && (
                    <>
                        <Typography variant="subtitle2" className={classes.secaoExpirados}>
                            Cupons expirados ({expirados.length})
                        </Typography>
                        <GradeCupons cupons={expirados} onAbrir={abrirEdicao} disabled={!!erroCarregamento} />
                    </>
                )}
            </div>

            <CupomDialog aberto={dialogAberto} cupomEdicao={cupomEdicao} onFechar={() => setDialogAberto(false)} />
        </div>
    )
}
